import { useLocation, useNavigate, useRouter, type AnyRouter } from '@tanstack/react-router';
import { Check, ChevronDown, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Separator,
} from 'react-aria-components';
import { Avatar } from '../components/Avatar.tsx';
import { Badge } from '../components/Badge.tsx';
import { can, roleLabel } from '../features/auth/permissions.ts';
import { announce } from '../state/announcer.ts';
import { authorizationOnly } from './authorizationOnly.ts';
import { refreshDirectory } from './directory.ts';
import { useSession } from '../state/session.ts';
import styles from './ShellMenus.module.css';

// The top bar's context: organization / workspace on the left, the signed-in person on the right.

interface Place {
  id: string;
  name: string;
  detail?: string | undefined;
  mono?: boolean;
  /** Your role there, on the right of the row. */
  role?: string | undefined;
  current: boolean;
}

interface Section {
  header?: ReactNode;
  places?: Place[];
  actions?: readonly (readonly [string, string] | false)[];
}

function ShellMenu({
  label,
  className = styles.trigger,
  trigger,
  sections,
  onAction,
  onOpen,
  placement = 'bottom start',
}: {
  label: string;
  className?: string;
  trigger: ReactNode;
  sections: Section[];
  onAction: (id: string) => void;
  onOpen?: () => void;
  placement?: 'bottom start' | 'bottom end';
}) {
  return (
    <MenuTrigger onOpenChange={(open) => open && onOpen?.()}>
      <AriaButton className={className} aria-label={label}>
        {trigger}
      </AriaButton>
      <Popover className={styles.popover} placement={placement} offset={6}>
        <Menu
          className={styles.menu}
          aria-label={label.split(' · ')[0]}
          onAction={(key) => onAction(String(key))}
        >
          {sections.map((section, i) => (
            <MenuSection key={i}>
              {i > 0 && <Separator className={styles.separator} />}
              {section.header && <Header className={styles.header}>{section.header}</Header>}
              {section.places?.map((place) => (
                <MenuItem
                  key={place.id}
                  id={place.id}
                  textValue={place.name}
                  aria-label={[place.name, place.detail, place.role, place.current && 'current']
                    .filter(Boolean)
                    .join(', ')}
                  className={styles.item}
                >
                  <Avatar name={place.name} kind="place" size="small" />
                  <span className={styles.itemText}>
                    <span className={styles.itemTitle}>{place.name}</span>
                    {place.detail && (
                      <span className={styles.itemDetail} data-mono={place.mono || undefined}>
                        {place.detail}
                      </span>
                    )}
                  </span>
                  {place.role && <span className={styles.itemRole}>{place.role}</span>}
                  <Check
                    size={16}
                    aria-hidden="true"
                    className={styles.check}
                    data-hidden={!place.current || undefined}
                  />
                </MenuItem>
              ))}
              {section.actions?.map(
                (action) =>
                  action && (
                    <MenuItem key={action[0]} id={action[0]} className={styles.item}>
                      {action[1]}
                    </MenuItem>
                  ),
              )}
            </MenuSection>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

// Changing context loads on first use; the shell itself carries none of it.
const switching = () => import('../features/workspace/switchWorkspace.ts');
const switchTo = (router: AnyRouter, id: string) =>
  void switching().then((m) => m.switchWorkspace(router, id));

const chevron = <ChevronDown size={14} aria-hidden="true" className={styles.chevron} />;
const slash = (
  <span className={styles.slash} aria-hidden="true">
    /
  </span>
);

export function OrganizationSwitcher() {
  const organization = useSession((s) => s.workspace?.organization);
  const orgs = useSession((s) => s.orgs);
  const navigate = useNavigate();
  const router = useRouter();
  if (!organization) return null;
  // Someone given only this workspace sees its organization as context, nothing to manage.
  const current = orgs.find((o) => o.id === organization.id);

  return (
    <span className={`${styles.crumbs} ${styles.orgCrumb}`}>
      {slash}
      <ShellMenu
        label={`Organization menu · ${organization.name}`}
        trigger={
          <>
            <Avatar name={organization.name} kind="place" size="small" />
            <span className={styles.triggerText}>{organization.name}</span>
            {chevron}
          </>
        }
        onOpen={refreshDirectory}
        sections={[
          ...(current
            ? []
            : [
                {
                  header: (
                    <span className={styles.identity}>
                      <span className={styles.itemTitle}>{organization.name}</span>
                      <span className={styles.itemDetail}>
                        You aren’t a member; this workspace only
                      </span>
                    </span>
                  ),
                },
              ]),
          {
            header: current ? 'Organizations' : 'Your organizations',
            places: orgs.map((org) => ({
              id: org.id,
              name: org.name,
              role: roleLabel(org.role),
              current: org.id === organization.id,
            })),
          },
          {
            actions: [
              !authorizationOnly() &&
                can(current, 'org.members.manage') && ['members', 'Invite and manage members'],
              !authorizationOnly() && Boolean(current) && ['settings', 'Organization settings'],
              ['all', 'All organizations and workspaces'],
            ],
          },
        ]}
        onAction={(id) => {
          if (id === 'members') void navigate({ to: '/settings/organization/members' });
          else if (id === 'all') void navigate({ to: '/workspaces' });
          else if (id === 'settings' || id === organization.id)
            void navigate({ to: '/settings/organization' });
          else void switching().then((m) => m.switchOrganization(router, id));
        }}
      />
    </span>
  );
}

/** The address of this page that opens in this workspace for whoever follows it. */
async function copyLink(workspaceId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('workspace', workspaceId);
  try {
    await navigator.clipboard.writeText(url.toString());
    announce('Link copied.');
  } catch {
    announce('Couldn’t copy the link. Copy it from the address bar.');
  }
}

export function WorkspaceSwitcher({ onNewWorkspace }: { onNewWorkspace: () => void }) {
  const workspace = useSession((s) => s.workspace);
  const workspaces = useSession((s) => s.workspaces);
  const pending = useSession((s) => s.switching);
  const signedIn = useSession((s) => Boolean(s.actor));
  const navigate = useNavigate();
  const router = useRouter();
  if (!workspace) return null;
  // The workspaces beside this one: the same organization, or the others outside any.
  const orgId = workspace.organization?.id ?? null;
  const siblings = workspaces.filter((ws) => (ws.organizationId ?? null) === orgId);
  const listed = siblings.some((ws) => ws.id === workspace.id)
    ? siblings
    : [workspace, ...siblings];

  return (
    <span className={styles.crumbs} aria-busy={Boolean(pending) || undefined}>
      {slash}
      <ShellMenu
        label={`Workspace menu · ${workspace.name} (${workspace.domain})`}
        className={`${styles.trigger} ${pending ? styles.pending : ''}`}
        trigger={
          <>
            <span className={`${styles.triggerText} ${styles.domain}`}>
              {pending ? pending.domain || pending.name : workspace.domain}
            </span>
            {chevron}
          </>
        }
        onOpen={refreshDirectory}
        sections={[
          {
            header: workspace.organization?.name ?? 'Workspaces',
            places: listed.map((ws) => ({
              id: ws.id,
              name: ws.name,
              detail: ws.domain,
              mono: true,
              role: ws.role ? roleLabel(ws.role) : undefined,
              current: ws.id === workspace.id,
            })),
          },
          {
            actions: [
              !authorizationOnly() &&
                signedIn &&
                can(workspace, 'workspace.members.manage') && [
                  'members',
                  'Invite and manage members',
                ],
              !authorizationOnly() && ['settings', 'Workspace settings'],
              signedIn && ['copy-link', 'Copy link to this page'],
              signedIn && ['all', 'All workspaces'],
              ['new', 'New workspace'],
            ],
          },
        ]}
        onAction={(id) => {
          if (id === 'members') void navigate({ to: '/settings/members' });
          else if (id === 'settings') void navigate({ to: '/settings/workspace' });
          else if (id === 'copy-link') void copyLink(workspace.id);
          else if (id === 'all') void navigate({ to: '/workspaces' });
          else if (id === 'new') onNewWorkspace();
          else if (id !== workspace.id) switchTo(router, id);
        }}
      />
      {!can(workspace, 'workspace.write') && (
        <span className={styles.viewOnly}>
          <Badge>View only</Badge>
        </span>
      )}
    </span>
  );
}

export function AccountMenu({ onSignOut }: { onSignOut: () => void }) {
  const actor = useSession((s) => s.actor);
  const navigate = useNavigate();
  const here = useLocation({ select: (l) => l.href });
  const name = actor ? actor.displayName || actor.email || 'Your account' : 'Not signed in';

  return (
    <ShellMenu
      label={`Account menu · ${name}`}
      className={styles.account}
      placement="bottom end"
      trigger={actor ? <Avatar name={name} /> : <UserRound size={18} aria-hidden="true" />}
      sections={[
        {
          header: (
            <span className={styles.identity}>
              <span className={styles.itemTitle}>{name}</span>
              {actor?.displayName && <span className={styles.itemDetail}>{actor.email}</span>}
            </span>
          ),
          actions: authorizationOnly()
            ? []
            : [Boolean(actor) && ['profile', 'Profile'], ['preferences', 'Preferences']],
        },
        { actions: [actor ? ['sign-out', 'Sign out'] : ['sign-in', 'Sign in']] },
      ]}
      onAction={(id) => {
        if (id === 'profile') void navigate({ to: '/settings/profile' });
        else if (id === 'preferences') void navigate({ to: '/settings/preferences' });
        else if (id === 'sign-in') void navigate({ to: '/sign-in', search: { redirect: here } });
        else onSignOut();
      }}
    />
  );
}
